import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  CreditCard, 
  ShoppingCart, 
  Plus, 
  Minus, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  ArrowLeft,
  Package,
  Lock,
  ExternalLink,
  RotateCcw,
  Search,
  User,
  X
} from 'lucide-react';
import { formatPhoneNumber } from '../utils/formatters';
import StripePaymentForm from '@/components/StripePaymentForm';

const API = process.env.REACT_APP_BACKEND_URL;

const InstructorEcardStore = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({});
  const [step, setStep] = useState('browse'); // browse, checkout, complete
  
  // Instructor selection state
  const [eligibleInstructors, setEligibleInstructors] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [instructorSearch, setInstructorSearch] = useState('');
  const [loadingInstructors, setLoadingInstructors] = useState(true);
  
  // Derived instructor info from selection
  const instructorInfo = useMemo(() => {
    if (!selectedInstructor) {
      return { name: '', email: '', phone: '', instructorNumber: '' };
    }
    return {
      name: selectedInstructor.name || '',
      email: selectedInstructor.email || '',
      phone: selectedInstructor.phone || '',
      instructorNumber: selectedInstructor.instructor_cards?.[0]?.number || ''
    };
  }, [selectedInstructor]);
  
  const [processing, setProcessing] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    phone: ''
  });

  useEffect(() => {
    fetchProducts();
    fetchEligibleInstructors();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/api/instructor/ecard-products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchEligibleInstructors = async () => {
    try {
      setLoadingInstructors(true);
      const response = await axios.get(`${API}/api/rentals/eligible-instructors`);
      setEligibleInstructors(response.data.instructors || []);
    } catch (error) {
      console.error('Failed to fetch instructors:', error);
      toast.error('Failed to load instructors');
    } finally {
      setLoadingInstructors(false);
    }
  };

  // Filter instructors based on search
  const filteredInstructors = useMemo(() => {
    if (!instructorSearch) return eligibleInstructors;
    const search = instructorSearch.toLowerCase();
    return eligibleInstructors.filter(inst =>
      inst.name?.toLowerCase().includes(search) ||
      inst.email?.toLowerCase().includes(search)
    );
  }, [eligibleInstructors, instructorSearch]);

  const updateQuantity = (productNumber, delta) => {
    const product = products.find(p => p.product_number === productNumber);
    const minOrder = product?.min_order || 1;
    const currentQty = cart[productNumber] || 0;
    let newQty = currentQty + delta;

    // When increasing from 0, jump to minimum
    if (delta > 0 && currentQty === 0 && minOrder > 1) {
      newQty = minOrder;
      toast.info(`Minimum order for ${product?.name || 'this item'} is ${minOrder}`);
    }
    // When decreasing at or below minimum, go to 0
    else if (delta < 0 && currentQty <= minOrder) {
      newQty = 0;
    }
    // Enforce minimum order quantity
    else if (newQty > 0 && newQty < minOrder) {
      newQty = minOrder;
      toast.info(`Minimum order for ${product?.name || 'this item'} is ${minOrder}`);
    }
    if (newQty < 0) newQty = 0;

    setCart(prev => ({
      ...prev,
      [productNumber]: newQty
    }));
  };

  const setQuantity = (productNumber, qty) => {
    const product = products.find(p => p.product_number === productNumber);
    const minOrder = product?.min_order || 1;
    
    // Allow empty string for typing, store it as empty
    if (qty === '' || qty === null || qty === undefined) {
      setCart(prev => ({
        ...prev,
        [productNumber]: ''
      }));
      return;
    }
    
    let newQty = parseInt(qty) || 0;

    // Enforce minimum order quantity with notification
    if (newQty > 0 && newQty < minOrder) {
      newQty = minOrder;
      toast.info(`Minimum order for ${product?.name || 'this item'} is ${minOrder}`);
    }

    setCart(prev => ({
      ...prev,
      [productNumber]: newQty
    }));
  };
  
  // Handler for onBlur to ensure valid value
  const handleQuantityBlur = (productNumber, qty) => {
    const product = products.find(p => p.product_number === productNumber);
    const minOrder = product?.min_order || 1;
    let newQty = parseInt(qty) || 0;
    
    // If they typed something but it's below minimum, enforce minimum
    if (newQty > 0 && newQty < minOrder) {
      newQty = minOrder;
    }
    
    setCart(prev => ({
      ...prev,
      [productNumber]: newQty
    }));
  };

  // Calculate if bulk discount applies for a product
  const getBulkDiscount = (productNumber, qty) => {
    const product = products.find(p => p.product_number === productNumber);
    if (!product) return { applies: false, discountPercent: 0, discountAmount: 0 };
    
    const bulkQty = product.bulk_quantity || 0;
    const bulkDiscountPercent = product.bulk_discount_percent || 0;
    
    if (bulkQty > 0 && bulkDiscountPercent > 0 && qty >= bulkQty) {
      const subtotal = (product.price || 0) * qty;
      const discountAmount = subtotal * (bulkDiscountPercent / 100);
      return { 
        applies: true, 
        discountPercent: bulkDiscountPercent, 
        discountAmount,
        bulkQty
      };
    }
    return { applies: false, discountPercent: 0, discountAmount: 0 };
  };

  // Get item subtotal after bulk discount
  const getItemSubtotal = (productNumber, qty) => {
    const product = products.find(p => p.product_number === productNumber);
    const baseSubtotal = (product?.price || 0) * qty;
    const bulk = getBulkDiscount(productNumber, qty);
    return baseSubtotal - bulk.discountAmount;
  };

  const getCartTotal = () => {
    return Object.entries(cart).reduce((total, [productNumber, qty]) => {
      return total + getItemSubtotal(productNumber, qty);
    }, 0);
  };

  const getTotalBulkDiscount = () => {
    return Object.entries(cart).reduce((total, [productNumber, qty]) => {
      const bulk = getBulkDiscount(productNumber, qty);
      return total + bulk.discountAmount;
    }, 0);
  };

  const getCartItemCount = () => {
    return Object.values(cart).reduce((sum, qty) => sum + qty, 0);
  };

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation - accepts formats like (555) 123-4567, 555-123-4567, 5551234567, +1 555 123 4567
  const validatePhone = (phone) => {
    if (!phone || phone.trim() === '') return true; // Optional field
    const phoneRegex = /^[\+]?[(]?[0-9]{1,3}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,9}$/;
    const digitsOnly = phone.replace(/\D/g, '');
    return phoneRegex.test(phone) && digitsOnly.length >= 10 && digitsOnly.length <= 15;
  };

  const handleEmailChange = (e) => {
    // Email is now read-only from instructor selection
    // This handler is kept for backwards compatibility but won't be used
  };

  const handlePhoneChange = (e) => {
    // Phone is now read-only from instructor selection
    // This handler is kept for backwards compatibility but won't be used
  };

  const canProceedToCheckout = () => {
    const hasItems = getCartItemCount() > 0;
    const hasInstructor = selectedInstructor !== null;
    const hasValidEmail = instructorInfo.email && validateEmail(instructorInfo.email);
    return hasItems && hasInstructor && hasValidEmail;
  };

  // Reset form - clear cart and instructor selection
  const resetForm = () => {
    setCart({});
    setSelectedInstructor(null);
    setInstructorSearch('');
    setValidationErrors({
      email: '',
      phone: ''
    });
    setTermsAccepted(false);
    setStep('browse');
  };

  // Build order data for Stripe
  const orderData = useMemo(() => {
    const items = Object.entries(cart)
      .filter(([_, qty]) => qty > 0)
      .map(([productNumber, qty]) => {
        const product = products.find(p => p.product_number === productNumber);
        const bulk = getBulkDiscount(productNumber, qty);
        const baseSubtotal = (product?.price || 0) * qty;
        return {
          product_number: productNumber,
          product_name: product?.name || productNumber,
          quantity: qty,
          unit_price: product?.price || 0,
          subtotal: baseSubtotal,
          bulk_discount_applied: bulk.applies,
          bulk_discount_percent: bulk.discountPercent,
          bulk_discount_amount: bulk.discountAmount,
          final_subtotal: baseSubtotal - bulk.discountAmount
        };
      });

    return {
      items,
      total: getCartTotal(),
      total_bulk_discount: getTotalBulkDiscount(),
      instructor_name: instructorInfo.name,
      instructor_email: instructorInfo.email,
      instructor_phone: instructorInfo.phone,
      instructor_number: instructorInfo.instructorNumber
    };
  }, [cart, products, instructorInfo]);

  // Handle successful payment
  const onStripeApprove = async (data) => {
    setProcessing(true);
    try {
      // Payment already completed via StripePaymentForm
      // Now create order in our system with the payment intent ID
      const orderResponse = await axios.post(`${API}/api/instructor/ecard-orders`, {
        ...orderData,
        payment_id: data.paymentIntentId,  // Use payment_id field expected by backend
        payment_status: 'completed'
      });

      setCompletedOrder({
        order_id: orderResponse.data.order_id,
        payment_id: data.paymentIntentId,
        total: getCartTotal()
      });
      setStep('complete');
      toast.success('Order placed successfully!');
    } catch (error) {
      console.error('Failed to complete order:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to complete order';
      toast.error(errorMessage + '. Please contact support if this persists.');
    } finally {
      setProcessing(false);
    }
  };

  // Handle payment errors
  const onStripeError = (err) => {
    console.error('Stripe error:', err);
    const errorMessage = err?.message || 'Payment failed. Please try again.';
    toast.error(errorMessage, { duration: 6000 });
    setProcessing(false);
  };

  // Handle $0 orders (no payment required)
  const createFreeOrder = async () => {
    setProcessing(true);
    try {
      // Build the order payload
      const orderPayload = {
        items: orderData.items.map(item => ({
          product_number: item.product_number,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          subtotal: item.final_subtotal || item.subtotal
        })),
        total: orderData.total,
        instructor_name: orderData.instructor_name,
        instructor_email: orderData.instructor_email,
        instructor_phone: orderData.instructor_phone || '',
        instructor_number: orderData.instructor_number || '',
        payment_id: 'FREE_ORDER',
        payment_status: 'completed'
      };
      
      console.log('Creating free order with payload:', orderPayload);
      
      // Create order in our system without payment
      const orderResponse = await axios.post(`${API}/api/instructor/ecard-orders`, orderPayload);

      setCompletedOrder({
        order_id: orderResponse.data.order_id,
        payment_id: 'FREE_ORDER',
        total: 0
      });
      setStep('complete');
      toast.success('Order placed successfully! (No payment required)');
    } catch (error) {
      console.error('Failed to complete order:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Failed to complete order: ' + (error.response?.data?.detail || 'Please contact support.'));
    } finally {
      setProcessing(false);
    }
  };

  // Stripe hosted fields styles
  const stripeFieldStyles = useMemo(() => ({
    '.valid': { color: '#22c55e' },
    '.invalid': { color: '#ef4444' },
    'input': { 
      'font-size': '16px',
      'font-family': 'inherit',
      'color': '#ffffff',
      'padding': '12px',
    }
  }), []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-400 mx-auto mb-4" />
          <p className="text-slate-400">Loading eCard Store...</p>
        </div>
      </div>
    );
  }

  if (step === 'complete') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
        <div className="max-w-lg mx-auto">
          <Card className="bg-slate-800 border-slate-700 text-slate-100">
            <CardHeader className="text-center">
              <div className="mx-auto bg-teal-500/20 p-4 rounded-full w-fit mb-4">
                <CheckCircle className="h-12 w-12 text-emerald-400" />
              </div>
              <CardTitle className="text-2xl text-emerald-400">Order Confirmed!</CardTitle>
              <CardDescription className="text-slate-400">
                Thank you for your purchase
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="flex justify-between mb-2">
                  <span className="text-slate-400">Order ID:</span>
                  <span className="font-mono text-emerald-400">{completedOrder?.order_id}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-400">Payment ID:</span>
                  <span className="font-mono text-xs text-white">{completedOrder?.payment_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Total:</span>
                  <span className="text-xl font-bold text-white">${completedOrder?.total?.toFixed(2)}</span>
                </div>
              </div>
              <p className="text-sm text-slate-400 text-center">
                A confirmation email will be sent to <span className="text-slate-200">{instructorInfo.email}</span>. 
                Your eCards will be processed and sent to you shortly.
              </p>
            </CardContent>
            <CardFooter>
              <Button 
                onClick={() => {
                  setStep('browse');
                  setCart({});
                  setCompletedOrder(null);
                  setTermsAccepted(false);
                }}
                className="w-full bg-teal-600 hover:bg-teal-500 text-white font-semibold"
                data-testid="place-another-order-btn"
              >
                Place Another Order
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <CreditCard className="h-8 w-8 text-emerald-400" />
            <h1 className="text-3xl font-bold text-white">Instructor eCard Store</h1>
          </div>
          <p className="text-slate-400">Purchase AHA eCards for your students</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.location.href = '/'}
            className="mt-4 text-slate-400 hover:text-white"
            data-testid="back-to-booking"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Booking Site
          </Button>
        </div>

        {step === 'browse' ? (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Products Grid */}
            <div className="lg:col-span-2 space-y-4">
              <h2 className="text-xl font-semibold text-slate-200 flex items-center gap-2">
                <Package className="h-5 w-5 text-purple-400" />
                Available eCards
              </h2>
              
              {products.length === 0 ? (
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="py-12 text-center">
                    <AlertCircle className="h-12 w-12 text-slate-500 mx-auto mb-4" />
                    <p className="text-slate-400">No eCards available at this time</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {products.map(product => {
                    const qty = cart[product.product_number] || 0;
                    const bulk = getBulkDiscount(product.product_number, qty);
                    const baseSubtotal = product.price * qty;
                    
                    return (
                    <Card key={product.product_number} className="bg-slate-800 border-slate-700">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg text-slate-100">{product.name}</CardTitle>
                        <CardDescription className="text-slate-400">
                          Product #: {product.product_number}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-2xl font-bold text-emerald-400">
                            ${product.price.toFixed(2)}
                          </span>
                          <span className="text-xs text-slate-500">
                            Min order: {product.min_order}
                          </span>
                        </div>
                        
                        {/* Bulk discount badge */}
                        {product.bulk_quantity > 0 && product.bulk_discount_percent > 0 && (
                          <div className="mb-3 px-2 py-1 bg-amber-500/20 border border-amber-500/30 rounded text-xs text-amber-300">
                            Buy {product.bulk_quantity}+ get {product.bulk_discount_percent}% off
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateQuantity(product.product_number, -1)}
                            disabled={qty === 0}
                            className="border-teal-600 text-white hover:bg-slate-700"
                            data-testid={`minus-btn-${product.product_number}`}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <Input
                            type="number"
                            min="0"
                            value={qty}
                            onChange={(e) => setQuantity(product.product_number, e.target.value)}
                            onBlur={(e) => handleQuantityBlur(product.product_number, e.target.value)}
                            className="w-20 text-center bg-slate-700 border-teal-600 text-slate-100"
                            data-testid={`qty-input-${product.product_number}`}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateQuantity(product.product_number, 1)}
                            className="border-teal-600 text-white hover:bg-slate-700"
                            data-testid={`plus-btn-${product.product_number}`}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        {qty > 0 && (
                          <div className="mt-3 text-right space-y-1">
                            {bulk.applies ? (
                              <>
                                <div className="text-slate-500 text-sm line-through">
                                  ${baseSubtotal.toFixed(2)}
                                </div>
                                <div className="text-xs text-amber-400">
                                  Bulk discount ({bulk.discountPercent}%): -${bulk.discountAmount.toFixed(2)}
                                </div>
                                <div>
                                  <span className="text-slate-400">Subtotal: </span>
                                  <span className="font-semibold text-emerald-400">
                                    ${(baseSubtotal - bulk.discountAmount).toFixed(2)}
                                  </span>
                                </div>
                              </>
                            ) : (
                              <div>
                                <span className="text-slate-400">Subtotal: </span>
                                <span className="font-semibold text-white">
                                  ${baseSubtotal.toFixed(2)}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )})}
                </div>
              )}
            </div>

            {/* Cart & Checkout Panel */}
            <div className="space-y-4">
              <Card className="bg-slate-800 border-slate-700 sticky top-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-slate-100">
                      <ShoppingCart className="h-5 w-5 text-emerald-400" />
                      Your Order
                    </CardTitle>
                    {(getCartItemCount() > 0 || selectedInstructor) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={resetForm}
                        className="text-slate-400 hover:text-slate-200 hover:bg-slate-700 h-8 px-2"
                        data-testid="reset-form-btn"
                      >
                        <RotateCcw className="h-4 w-4 mr-1" />
                        Reset
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Cart Items */}
                  {Object.entries(cart).filter(([_, qty]) => qty > 0).length === 0 ? (
                    <p className="text-slate-400 text-center py-4">Your cart is empty</p>
                  ) : (
                    <div className="space-y-2">
                      {Object.entries(cart)
                        .filter(([_, qty]) => qty > 0)
                        .map(([productNumber, qty]) => {
                          const product = products.find(p => p.product_number === productNumber);
                          const bulk = getBulkDiscount(productNumber, qty);
                          const basePrice = (product?.price || 0) * qty;
                          return (
                            <div key={productNumber} className="text-sm">
                              <div className="flex justify-between">
                                <span className="text-white">
                                  {product?.name} x{qty}
                                </span>
                                <span className={bulk.applies ? "text-slate-500 line-through" : "text-slate-100"}>
                                  ${basePrice.toFixed(2)}
                                </span>
                              </div>
                              {bulk.applies && (
                                <div className="flex justify-between text-xs text-amber-400 mt-0.5">
                                  <span>Bulk discount ({bulk.discountPercent}%)</span>
                                  <span>-${bulk.discountAmount.toFixed(2)}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}

                  <Separator className="bg-slate-700" />

                  {/* Bulk Discount Total (if any) */}
                  {getTotalBulkDiscount() > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-amber-400">
                        <span>Total Bulk Savings</span>
                        <span>-${getTotalBulkDiscount().toFixed(2)}</span>
                      </div>
                      <Separator className="bg-slate-700" />
                    </>
                  )}

                  {/* Total */}
                  <div className="flex justify-between text-lg font-semibold">
                    <span className="text-slate-200">Total</span>
                    <span className="text-emerald-400">${getCartTotal().toFixed(2)}</span>
                  </div>

                  <Separator className="bg-slate-700" />

                  {/* Instructor Selection Dropdown */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-white flex items-center gap-2">
                      <User className="h-4 w-4 text-teal-400" />
                      Select Instructor
                    </h3>
                    
                    {loadingInstructors ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-teal-400" />
                        <span className="ml-2 text-slate-400 text-sm">Loading instructors...</span>
                      </div>
                    ) : selectedInstructor ? (
                      /* Selected Instructor Display */
                      <div className="bg-slate-700/50 rounded-lg p-3 border border-teal-600">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">{selectedInstructor.name}</p>
                            <p className="text-sm text-slate-400">{selectedInstructor.email}</p>
                            {selectedInstructor.phone && (
                              <p className="text-sm text-slate-400">{selectedInstructor.phone}</p>
                            )}
                            {selectedInstructor.instructor_cards?.[0] && (
                              <p className="text-xs text-teal-400 mt-1">
                                {selectedInstructor.instructor_cards[0].type} • #{selectedInstructor.instructor_cards[0].number}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedInstructor(null);
                              setInstructorSearch('');
                            }}
                            className="text-slate-400 hover:text-white hover:bg-slate-600"
                            data-testid="change-instructor-btn"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* Instructor Search & Selection */
                      <div className="space-y-2">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            placeholder="Search instructors..."
                            value={instructorSearch}
                            onChange={(e) => setInstructorSearch(e.target.value)}
                            className="bg-slate-700 border-teal-600 text-slate-100 pl-9"
                            data-testid="instructor-search-input"
                          />
                        </div>
                        
                        <div className="max-h-48 overflow-y-auto space-y-1 bg-slate-700/30 rounded-lg p-2">
                          {filteredInstructors.length === 0 ? (
                            <div className="text-center py-4 text-slate-400">
                              <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">No eligible instructors found</p>
                              <p className="text-xs mt-1">Only instructors with valid, non-expired cards are shown</p>
                            </div>
                          ) : (
                            filteredInstructors.map(instructor => (
                              <button
                                key={instructor.id}
                                onClick={() => setSelectedInstructor(instructor)}
                                className="w-full text-left p-2 rounded hover:bg-slate-600/50 transition-colors"
                                data-testid={`instructor-option-${instructor.id}`}
                              >
                                <p className="text-white font-medium text-sm">{instructor.name}</p>
                                <p className="text-xs text-slate-400">{instructor.email}</p>
                                {instructor.instructor_cards?.[0] && (
                                  <p className="text-xs text-teal-400/70">
                                    {instructor.instructor_cards[0].type}
                                  </p>
                                )}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    onClick={() => setStep('checkout')}
                    disabled={!canProceedToCheckout()}
                    className="w-full bg-teal-600 hover:bg-teal-500 text-white font-semibold disabled:opacity-50"
                    data-testid="proceed-to-payment-btn"
                  >
                    Proceed to Payment
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>
        ) : (
          /* Checkout Step */
          <div className="max-w-xl mx-auto">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setStep('browse');
                    setTermsAccepted(false);
                  }}
                  className="w-fit text-slate-400 hover:text-slate-200 -ml-2 mb-2"
                  data-testid="back-to-cart-btn"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Cart
                </Button>
                <CardTitle className="text-slate-100">Complete Your Purchase</CardTitle>
                <CardDescription className="text-slate-400">
                  Review your order and complete payment
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Order Summary */}
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <h3 className="font-medium text-slate-200 mb-3">Order Summary</h3>
                  <div className="space-y-2">
                    {Object.entries(cart)
                      .filter(([_, qty]) => qty > 0)
                      .map(([productNumber, qty]) => {
                        const product = products.find(p => p.product_number === productNumber);
                        const bulk = getBulkDiscount(productNumber, qty);
                        const basePrice = (product?.price || 0) * qty;
                        return (
                          <div key={productNumber} className="text-sm">
                            <div className="flex justify-between">
                              <span className="text-white">
                                {product?.name} x{qty}
                              </span>
                              <span className={bulk.applies ? "text-slate-500 line-through" : "text-slate-100"}>
                                ${basePrice.toFixed(2)}
                              </span>
                            </div>
                            {bulk.applies && (
                              <div className="flex justify-between text-xs text-amber-400 mt-1 pl-2">
                                <span>Bulk Order Discount ({bulk.discountPercent}%)</span>
                                <span>-${bulk.discountAmount.toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    <Separator className="bg-slate-600 my-2" />
                    {getTotalBulkDiscount() > 0 && (
                      <div className="flex justify-between text-sm text-amber-400">
                        <span>Total Bulk Savings</span>
                        <span>-${getTotalBulkDiscount().toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold">
                      <span className="text-slate-200">Total</span>
                      <span className="text-emerald-400">${getCartTotal().toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Customer Info */}
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <h3 className="font-medium text-slate-200 mb-2">Purchaser Information</h3>
                  <div className="text-sm text-white">
                    <p>{instructorInfo.name}</p>
                    {instructorInfo.instructorNumber && <p className="text-slate-400">Instructor #: {instructorInfo.instructorNumber}</p>}
                    <p>{instructorInfo.email}</p>
                    {instructorInfo.phone && <p>{instructorInfo.phone}</p>}
                  </div>
                </div>

                {/* Stripe Advanced Checkout or Free Order */}
                <div>
                  <h3 className="font-medium text-slate-200 mb-3">Payment</h3>
                  {processing ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-emerald-400 mr-3" />
                      <span className="text-white">Processing order...</span>
                    </div>
                  ) : getCartTotal() === 0 ? (
                    /* Free Order - No Payment Required */
                    <div className="space-y-4">
                      <div className="bg-emerald-900/30 border border-emerald-600 rounded-lg p-4 text-center">
                        <CheckCircle className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                        <p className="text-emerald-400 font-medium">No Payment Required</p>
                        <p className="text-slate-400 text-sm mt-1">Your order total is $0.00</p>
                      </div>
                      
                      {/* Terms and Conditions Checkbox */}
                      <div className="flex items-start gap-3 py-2">
                        <Checkbox 
                          id="free-order-terms"
                          checked={termsAccepted}
                          onCheckedChange={setTermsAccepted}
                          className="border-teal-600 data-[state=checked]:bg-teal-600"
                          data-testid="free-order-terms-checkbox"
                        />
                        <Label htmlFor="free-order-terms" className="text-sm text-slate-300 leading-relaxed cursor-pointer">
                          I understand that eCards purchased here are for my instructor use only and are non-refundable.
                        </Label>
                      </div>
                      
                      <Button
                        onClick={createFreeOrder}
                        disabled={!termsAccepted}
                        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold py-3 disabled:opacity-50"
                        data-testid="complete-free-order-btn"
                      >
                        Complete Order
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Terms and Conditions Checkbox */}
                      <div className="flex items-start gap-3 py-2">
                        <Checkbox 
                          id="payment-terms"
                          checked={termsAccepted}
                          onCheckedChange={setTermsAccepted}
                          className="mt-0.5 border-slate-500 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                          data-testid="terms-checkbox"
                        />
                        <label htmlFor="payment-terms" className="text-sm text-white leading-snug cursor-pointer">
                          I agree to the{' '}
                          <a 
                            href="https://www.biggestlittlecpr.com/terms-conditions/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-emerald-400 hover:text-emerald-300 underline inline-flex items-center gap-1"
                          >
                            Terms and Conditions
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </label>
                      </div>
                      
                      {/* Stripe Payment Form */}
                      <StripePaymentForm
                        amount={getCartTotal()}
                        description={`eCard Store Order - ${Object.keys(cart).filter(k => cart[k] > 0).length} item(s)`}
                        customerEmail={instructorInfo.email}
                        customerName={instructorInfo.name}
                        customerPhone={instructorInfo.phone}
                        metadata={{
                          instructor_email: instructorInfo.email,
                          instructor_number: instructorInfo.instructorNumber,
                          item_count: Object.keys(cart).filter(k => cart[k] > 0).length.toString()
                        }}
                        onSuccess={onStripeApprove}
                        onError={onStripeError}
                        disabled={processing}
                        termsAccepted={termsAccepted}
                      />
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1">
                  <Lock className="h-3 w-3" />
                  Secure payment powered by Stripe
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructorEcardStore;
